import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EtapaProgramada } from '@pcp/contracts';
import { useLogout, useMe, usePlanSteps, usePlans } from '../api/queries';
import { StepStatusChip } from '../components/chips';
import { EmptyState, ErrorState, LoadingBlock } from '../components/states';
import { ThemeToggle } from '../components/ThemeToggle';
import {
  formatKg,
  formatMinutes,
  formatThickness,
  formatTime,
  formatWeekdayDate,
  formatWidth,
} from '../format';
import { t } from '../i18n/pt-BR';

const FIVE_MINUTES = 5 * 60_000;

/** Descreve o corte da Slitter: `3×320 em 1.000 · 96%` (só formata o que a API devolveu). */
const cutLabel = (e: EtapaProgramada) =>
  e['qtd-pistas'] !== undefined && e['largura-mae'] !== undefined
    ? `${e['qtd-pistas']}×${formatWidth(e.largura)} em ${formatWidth(e['largura-mae'])} · ${e['pct-aproveit'] ?? ''}%`
    : '';

/** Visão do operador (tablet): só a máquina dele, hoje e amanhã, só leitura. */
export function OperatorPage() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const plans = usePlans();
  const published = plans.data?.find((p) => p.situacao === 2);
  const steps = usePlanSteps(published?.['nr-plano'], {}, { refetchInterval: FIVE_MINUTES });

  const byDate = useMemo(() => {
    const groups = new Map<string, EtapaProgramada[]>();
    for (const e of steps.data ?? []) groups.set(e['dt-prog'], [...(groups.get(e['dt-prog']) ?? []), e]);
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [steps.data]);

  if (!me) return null;

  return (
    <div className="operator app">
      <header className="topbar">
        <div className="wrap">
          <div className="brand">
            <img className="logo" src="/acg-logo.png" alt={t.app.logoAlt} width={78} height={44} />
            <div>
              <h1>{t.operator.title}</h1>
              <div className="site">
                {t.operator.machine}: <span className="big-machine">{me.maquina ?? '—'}</span>
              </div>
            </div>
          </div>
          <div className="topbar-actions">
            <ThemeToggle />
            <span className="user">{me.nome}</span>
            <button
              type="button"
              className="btn ghost big"
              onClick={() =>
                logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })
              }
            >
              {t.auth.logout}
            </button>
          </div>
        </div>
      </header>
      <main className="wrap">
        {!me.maquina ? <EmptyState title={t.operator.title}>{t.operator.noMachine}</EmptyState> : null}
        {plans.isLoading || (published && steps.isLoading) ? <LoadingBlock rows={5} /> : null}
        {plans.error ? <ErrorState error={plans.error} onRetry={() => void plans.refetch()} /> : null}
        {plans.data && !published ? (
          <EmptyState title={t.operator.title}>{t.operator.noPublished}</EmptyState>
        ) : null}
        {steps.error ? <ErrorState error={steps.error} onRetry={() => void steps.refetch()} /> : null}
        {published && steps.data && !byDate.length ? (
          <EmptyState title={t.operator.title}>{t.operator.empty}</EmptyState>
        ) : null}

        {byDate.map(([date, list]) => (
          <section key={date} className="panel" aria-label={formatWeekdayDate(date)}>
            <h2>{formatWeekdayDate(date)}</h2>
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th className="n">{t.operator.cols.sequence}</th>
                    <th>{t.operator.cols.item}</th>
                    <th>{t.operator.cols.order}</th>
                    <th>{t.operator.cols.customer}</th>
                    <th className="n">{t.operator.cols.kg}</th>
                    <th className="n">{t.operator.cols.thickness}</th>
                    <th className="n">{t.operator.cols.width}</th>
                    <th>{t.operator.cols.cut}</th>
                    <th>{t.operator.cols.setup}</th>
                    <th>{t.operator.cols.start}</th>
                    <th>{t.operator.cols.end}</th>
                    <th>{t.operator.cols.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((e) => {
                    const reasons = e['motivos-setup'] ?? [];
                    return (
                      <tr key={e['id-prog']}>
                        <td className="n">{e.sequencia}</td>
                        <td>{e['descricao-item']}</td>
                        <td className="mono">{e['nr-pedcli']}</td>
                        <td>{e['nome-abrev']}</td>
                        <td className="n">{formatKg(e['qt-planejada'])}</td>
                        <td className="n">{formatThickness(e.espessura)}</td>
                        <td className="n">{formatWidth(e.largura)}</td>
                        <td className="mono">{cutLabel(e)}</td>
                        <td title={reasons.join(' · ')}>
                          {e['horas-setup'] > 0 ? formatMinutes(e['horas-setup'] * 60) : '–'}
                          {reasons.length ? <div className="hint">{reasons.join(' · ')}</div> : null}
                        </td>
                        <td className="mono">{formatTime(e['dt-hr-inicio'])}</td>
                        <td className="mono">{formatTime(e['dt-hr-fim'])}</td>
                        <td>
                          <StepStatusChip situacao={e.situacao} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}
        <p className="hint">{t.operator.refreshEvery}</p>
      </main>
    </div>
  );
}
